import type { RedactionRect } from './redactionRect';

export type ImageFraction = {
  x: number;
  y: number;
};

export type PixelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function getRenderedContentDimensions(
  elementWidth: number,
  elementHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number; insetX: number; insetY: number } {
  if (elementWidth <= 0 || elementHeight <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    return {
      width: elementWidth,
      height: elementHeight,
      insetX: 0,
      insetY: 0,
    };
  }

  const imageAspect = naturalWidth / naturalHeight;
  const boxAspect = elementWidth / elementHeight;

  let width = elementWidth;
  let height = elementHeight;
  let insetX = 0;
  let insetY = 0;

  if (imageAspect > boxAspect) {
    height = elementWidth / imageAspect;
    insetY = (elementHeight - height) / 2;
  } else if (imageAspect < boxAspect) {
    width = elementHeight * imageAspect;
    insetX = (elementWidth - width) / 2;
  }

  return { width, height, insetX, insetY };
}

export function getRenderedImageBounds(image: HTMLImageElement): DOMRect | null {
  const box = image.getBoundingClientRect();

  if (box.width <= 0 || box.height <= 0) {
    return null;
  }

  return new DOMRect(box.left, box.top, box.width, box.height);
}

export function getRenderedImageLayout(
  image: HTMLImageElement,
  wrapper: HTMLElement,
): { left: number; top: number; width: number; height: number } | null {
  const rendered = getRenderedImageBounds(image);
  const wrapperRect = wrapper.getBoundingClientRect();

  if (!rendered) {
    return null;
  }

  return {
    left: rendered.left - wrapperRect.left,
    top: rendered.top - wrapperRect.top,
    width: rendered.width,
    height: rendered.height,
  };
}

export function clientPointToImageFraction(
  clientX: number,
  clientY: number,
  imageBounds: DOMRect,
): ImageFraction | null {
  if (imageBounds.width <= 0 || imageBounds.height <= 0) {
    return null;
  }

  const x = (clientX - imageBounds.left) / imageBounds.width;
  const y = (clientY - imageBounds.top) / imageBounds.height;

  if (x < 0 || x > 1 || y < 0 || y > 1) {
    return null;
  }

  return { x, y };
}

export function clientPointToImageFractionClamped(
  clientX: number,
  clientY: number,
  imageBounds: DOMRect,
): ImageFraction {
  if (imageBounds.width <= 0 || imageBounds.height <= 0) {
    return { x: 0, y: 0 };
  }

  return {
    x: Math.max(0, Math.min(1, (clientX - imageBounds.left) / imageBounds.width)),
    y: Math.max(0, Math.min(1, (clientY - imageBounds.top) / imageBounds.height)),
  };
}

/** Map a fractional rect to integer pixel edges on the source image. */
export function fractionRectToPixelRect(
  rect: Pick<RedactionRect, 'x' | 'y' | 'width' | 'height'>,
  imageWidth: number,
  imageHeight: number,
): PixelRect {
  const x1 = Math.round(rect.x * imageWidth);
  const y1 = Math.round(rect.y * imageHeight);
  const x2 = Math.round((rect.x + rect.width) * imageWidth);
  const y2 = Math.round((rect.y + rect.height) * imageHeight);

  const left = Math.max(0, Math.min(imageWidth, Math.min(x1, x2)));
  const top = Math.max(0, Math.min(imageHeight, Math.min(y1, y2)));
  const right = Math.max(0, Math.min(imageWidth, Math.max(x1, x2)));
  const bottom = Math.max(0, Math.min(imageHeight, Math.max(y1, y2)));

  return {
    x: left,
    y: top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}
